import { useWallet } from '@solana/wallet-adapter-react';
import { PhantomWalletName } from '@solana/wallet-adapter-phantom';
import { X, CheckCircle2, XCircle } from 'lucide-react';
import { create } from 'zustand';

interface WalletModalState {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

export const useWalletModal = create<WalletModalState>((set) => ({
  isOpen: false,
  setOpen: (open) => set({ isOpen: open }),
}));

export default function SolanaWalletModal() {
  const { select, wallets, connecting } = useWallet();
  const { isOpen, setOpen } = useWalletModal();

  if (!isOpen) return null;

  const getWalletStatus = (name: string) => {
    const wallet = wallets.find(w => w.adapter.name === name);
    return {
      isInstalled: wallet?.adapter.readyState === 'Installed',
      isLoadable: wallet?.adapter.readyState === 'Loadable',
      isNotDetected: wallet?.adapter.readyState === 'NotDetected' || !wallet
    };
  };

  const handleWalletSelect = async (name: any, downloadUrl?: string) => {
    try {
      const { isInstalled } = getWalletStatus(name);

      if (!isInstalled && downloadUrl) {
        window.open(downloadUrl, '_blank');
        return;
      }

      await select(name);
      setOpen(false);
    } catch (error) {
      console.error('Failed to connect wallet:', error);
    }
  };

  const phantomStatus = getWalletStatus(PhantomWalletName);
  const solflareStatus = getWalletStatus('Solflare');

  return (
    <div className="fixed inset-0 bg-quantum-dark/80 backdrop-blur-lg flex items-center justify-center z-50">
      <div className="quantum-card w-full max-w-md p-6 relative">
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 text-quantum-light/50 hover:text-quantum-light"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-xl font-bold text-quantum-light mb-6">Connect Wallet</h3>

        <div className="space-y-3">
          <button
            onClick={() => handleWalletSelect(PhantomWalletName, 'https://phantom.app/download')}
            disabled={connecting}
            className="quantum-card w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors duration-200"
          >
            <img
              src="/icons/phantom.webp"
              alt="Phantom"
              className="w-8 h-8 rounded-full"
            />
            <div className="flex-1 text-left">
              <div className="font-medium text-quantum-light">Phantom</div>
              <div className="text-sm text-quantum-light/70">
                {phantomStatus.isInstalled ? 'Connect to Phantom Wallet' : 'Install Phantom Wallet'}
              </div>
            </div>
            {phantomStatus.isInstalled ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
          </button>

          <button
            onClick={() => handleWalletSelect('Solflare', 'https://solflare.com/download')}
            disabled={connecting}
            className="quantum-card w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors duration-200"
          >
            <img
              src="/icons/solflare.ico"
              alt="Solflare"
              className="w-8 h-8 rounded-full"
            />
            <div className="flex-1 text-left">
              <div className="font-medium text-quantum-light">Solflare</div>
              <div className="text-sm text-quantum-light/70">
                {solflareStatus.isInstalled ? 'Connect to Solflare Wallet' : 'Install Solflare Wallet'}
              </div>
            </div>
            {solflareStatus.isInstalled ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
          </button>
        </div>

        {connecting && (
          <div className="mt-4 text-center text-quantum-light/70">
            Connecting...
          </div>
        )}

        <div className="mt-4 text-xs text-quantum-light/50 text-center">
          {!phantomStatus.isInstalled && !solflareStatus.isInstalled ? (
            "No Solana wallets detected. Please install one to continue."
          ) : (
            "Select a wallet to connect"
          )}
        </div>
      </div>
    </div>
  );
}